import { File, FolderOpen, X } from 'lucide-react';
import { C } from '../../theme.js';
import { Button, FieldLabel, IconButton, TextInput } from '../../components/Primitives.jsx';
import { ColSelect } from '../shared/ColSelect.jsx';

export function JoinInspector({ step, onChange, onBrowseJoinFile, secondaryDatasets }) {
  const c = step.config || {};
  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });

  // Normalize keys (backward compat with legacy on/rightOn)
  const keys = (c.keys && c.keys.length) ? c.keys : [{ left: c.on || '', right: c.rightOn || '' }];

  // Right dataset info
  const rightData = secondaryDatasets?.get(c.right);
  const rightCols = rightData?.columns || [];

  const updateKey = (i, field, value) => {
    const next = keys.map((k, j) => j === i ? { ...k, [field]: value } : k);
    u({ keys: next });
  };
  const addKey = () => u({ keys: [...keys, { left: '', right: '' }] });
  const removeKey = (i) => u({ keys: keys.filter((_, j) => j !== i) });

  return (
    <div>
      {/* Right dataset card */}
      <FieldLabel>Right dataset</FieldLabel>
      <div style={{
        background: C.input, border: `1px solid ${C.border}`, borderRadius: 8,
        padding: '10px 12px', marginBottom: 12,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ display: 'inline-flex', color: C.fg4 }}><File size={16} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              color: c.right ? C.fg1 : C.fg5,
              fontSize: '.82rem', fontWeight: 600,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{c.right || 'No file selected'}</div>
            {rightData && (
              <div style={{ color: C.fg5, fontSize: '.72rem', marginTop: 2 }}>
                {rightData.rows.length} rows · {rightCols.length} columns
              </div>
            )}
            {!rightData && c.right && (
              <div style={{ color: C.fg5, fontSize: '.72rem', marginTop: 2 }}>
                No data loaded
              </div>
            )}
          </div>
          <Button kind="ghost" size="sm" icon={<FolderOpen size={14} />} onClick={() => onBrowseJoinFile?.(step.id)}>
            Browse…
          </Button>
        </div>
      </div>

      {/* Join kind selector */}
      <FieldLabel>Join kind</FieldLabel>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 4 }}>
        {[
          { id: 'inner', label: 'Inner', desc: 'Both sides matched' },
          { id: 'left', label: 'Left', desc: 'All left rows' },
          { id: 'right', label: 'Right', desc: 'All right rows' },
          { id: 'outer', label: 'Outer', desc: 'All rows, fill nulls' },
          { id: 'cross', label: 'Cross', desc: 'Cartesian product' },
        ].map(k => {
          const on = (c.kind || 'inner') === k.id;
          return (
            <button key={k.id} onClick={() => u({ kind: k.id })}
              style={{
                fontFamily: 'inherit', cursor: 'pointer', textAlign: 'left',
                background: on ? 'var(--info-bg)' : C.input,
                color: on ? C.accentBright : C.fg3,
                border: `1px solid ${on ? C.accentMuted : C.border}`,
                borderRadius: 8, padding: '8px 10px',
                fontSize: '.78rem', fontWeight: 600,
              }}>
              <div>{k.label}</div>
              <div style={{ color: C.fg5, fontSize: '.7rem', marginTop: 2 }}>{k.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Join keys */}
      <FieldLabel hint={keys.length > 1 ? `${keys.length} key pairs` : undefined}>Join keys</FieldLabel>
      {keys.map((kp, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
          <ColSelect value={kp.left} onChange={(v) => updateKey(i, 'left', v)} placeholder="left column" style={{ flex: 1 }} />
          <span style={{ color: C.fg5, fontSize: '.78rem', flexShrink: 0 }}>=</span>
          {rightCols.length > 0
            ? <ColSelect columns={rightCols} value={kp.right} onChange={(v) => updateKey(i, 'right', v)} placeholder="right column" style={{ flex: 1 }} />
            : <TextInput mono value={kp.right} onChange={(v) => updateKey(i, 'right', v)} placeholder="right_col" style={{ flex: 1 }} />
          }
          {keys.length > 1 && (
            <IconButton onClick={() => removeKey(i)} title="Remove key pair"><X size={14} /></IconButton>
          )}
        </div>
      ))}
      <Button kind="ghost" size="sm" onClick={addKey}>＋ Add key pair</Button>

      {/* Right column prefix */}
      <FieldLabel hint="Avoid name collisions">Right column prefix</FieldLabel>
      <TextInput mono value={c.rightPrefix || ''} onChange={(v) => u({ rightPrefix: v })} placeholder="e.g. r_" />

      {/* Right dataset columns preview */}
      {rightData && rightCols.length > 0 && (
        <>
          <FieldLabel>Right columns</FieldLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {rightCols.map(col => (
              <span key={col.name} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: C.input, border: `1px solid ${C.border}`,
                borderRadius: 6, padding: '3px 8px',
                fontSize: '.72rem', color: C.fg3,
              }}>
                {col.name}
                <span style={{ color: C.fg5, fontSize: '.65rem' }}>{col.type}</span>
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
